import { hasFiredPwaInstallSuccess } from "@configs/analytics/events/dedupe";
import { STORAGE_KEY } from "@constants";
import { getRuntimeContext } from "@utils/pwa";
import { readJSONFromStorage, writeJSONToStorage } from "@utils/storage";
import { getPersistedDueAt } from "kz-ui-sdk";
import moment from "moment";

export const EVENT_INSTALL_BANNER_DISMISSED = "installBannerDismissed";

const INSTALL_BANNER_COOLDOWN_HOURS = 72;

export interface ProfileBadgeDownloadRowState {
  badgeSeen?: boolean;
}

// Per member, device-scoped.
function storageKey(memberId: string): string {
  return `${STORAGE_KEY.PROFILE_BADGE_DOWNLOAD_ROW_STATE}:${memberId}`;
}

export function readProfileBadgeDownloadRowState(memberId?: string): ProfileBadgeDownloadRowState {
  if (!memberId) return {};
  return readJSONFromStorage<ProfileBadgeDownloadRowState>(storageKey(memberId), {});
}

export function patchProfileBadgeDownloadRowState(
  memberId: string | undefined,
  patch: ProfileBadgeDownloadRowState,
): ProfileBadgeDownloadRowState {
  const next = { ...readProfileBadgeDownloadRowState(memberId), ...patch };
  if (memberId) writeJSONToStorage(storageKey(memberId), next);
  return next;
}

// The banner persists its next display time (dismiss + 72h). The 365-day value written on install
// falls outside the window, so this only matches a dismiss in the last 72h.
export function isInstallBannerCooldownActive(): boolean {
  const now = moment();
  const windowEnd = now.clone().add(INSTALL_BANNER_COOLDOWN_HOURS, "hours");
  return [STORAGE_KEY.PWA_REMINDER, STORAGE_KEY.EXTERNAL_INSTALLER_REMINDER].some((key) => {
    const dueAt = getPersistedDueAt(key);
    return !!dueAt && dueAt.isAfter(now) && !dueAt.isAfter(windowEnd);
  });
}

// Read once when this module loads, before anything on this page can write the `pwa_install_success` mark, so the
// order of `appinstalled`, the app window opening and the mark being written doesn't matter.
const isNewInstallDevice = !hasFiredPwaInstallSuccess();
const PENDING_CHANGE_EVENT = "pwaInstallRewardPendingChange";

// Set only for a new install (on `appinstalled` in the browser, or on an installed-app open) when this page loaded
// before the mark existed. Existing installs already had the mark, so they never get it. Cleared once the reward settles.
export function markPwaInstallRewardPendingIfNewInstall(): void {
  if (!isNewInstallDevice || isPwaInstallRewardPending()) return;
  try {
    localStorage.setItem(STORAGE_KEY.PWA_INSTALL_REWARD_PENDING, "1");
    window.dispatchEvent(new Event(PENDING_CHANGE_EVENT));
  } catch {
    // Storage blocked: no reward on this device.
  }
}

export function isPwaInstallRewardPending(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY.PWA_INSTALL_REWARD_PENDING);
  } catch {
    return false;
  }
}

export function clearPwaInstallRewardPending(): void {
  try {
    localStorage.removeItem(STORAGE_KEY.PWA_INSTALL_REWARD_PENDING);
    window.dispatchEvent(new Event(PENDING_CHANGE_EVENT));
  } catch {
    // Storage blocked; the backend's grantedAt still stops a second grant.
  }
}

export function getPwaInstallRewardPendingInApp(): boolean {
  return getRuntimeContext() === "pwa" && isPwaInstallRewardPending();
}

// Re-check when the page switches to app mode  or the
// flag changes here or in another window (the browser tab and the app window share storage).
export function subscribePwaInstallRewardPending(onChange: () => void): () => void {
  const media = window.matchMedia("(display-mode: standalone)");
  media.addEventListener("change", onChange);
  window.addEventListener(PENDING_CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    media.removeEventListener("change", onChange);
    window.removeEventListener(PENDING_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}
